import { LivePayload } from './led-board.model';

/** How long a design holds the board before the next one in line takes over. */
export const DEFAULT_HOLD_MS = 30000;

/**
 * One design waiting its turn, at /ledboard/queue/<key>. The payload fields are
 * the same shape the board already renders, so the Pi promotes an entry by
 * copying them to /ledboard/live — nothing new to decode.
 */
export interface QueueEntry extends LivePayload {
  key?: string;
  /** Optional label shown in the line. */
  name?: string;
  /** Small data URL so other phones can see what is waiting. */
  thumbnail?: string;
  /** Milliseconds this design holds the board. */
  holdMs: number;
  /** Client clock, for display only — ordering follows the push key. */
  submittedAt: number;
}

/** What the board is showing right now, written by the Pi at /ledboard/now. */
export interface NowShowing {
  /** The queue key this came from, or null when the board is showing something else. */
  id: string | null;
  name?: string | null;
  thumbnail?: string | null;
  startedAt: number;
  endsAt: number;
  holdMs: number;
}

/** Milliseconds until this design's turn ends; 0 once it is over. */
export function remainingMs(now: NowShowing | null, at: number): number {
  if (!now) { return 0; }
  return Math.max(0, now.endsAt - at);
}

/**
 * Position in the line, counting from 1, or 0 when the design is not waiting
 * (it is on the board already, or it was never queued).
 */
export function positionOf(key: string | null, queue: QueueEntry[]): number {
  if (!key) { return 0; }
  const i = queue.findIndex(q => q.key === key);
  return i < 0 ? 0 : i + 1;
}

/**
 * Rough wait before a queued design lights up: what is left of the current
 * turn, plus a full turn for everyone ahead of it.
 */
export function waitMs(key: string | null, queue: QueueEntry[], now: NowShowing | null, at: number): number {
  const pos = positionOf(key, queue);
  if (!pos) { return 0; }
  const ahead = queue.slice(0, pos - 1).reduce((sum, q) => sum + (q.holdMs || DEFAULT_HOLD_MS), 0);
  return remainingMs(now, at) + ahead;
}

/** "about 40 seconds", "about 2 minutes" — never a bare millisecond count. */
export function humanWait(ms: number): string {
  if (ms <= 1500) { return 'any second now'; }
  const secs = Math.round(ms / 1000);
  if (secs < 60) { return `about ${secs} seconds`; }
  const mins = Math.round(secs / 60);
  return mins === 1 ? 'about a minute' : `about ${mins} minutes`;
}
